import React, { useEffect } from "react";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import { IconButton, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../redux";
import {
  addEngagementCategorySagaAction,
  deleteEngagementCategorySagaAction,
  getPartnerEngagementCategoryListSagaAction,
  updateEngagementCategorySagaAction,
  EngagementCategoryPayloadType,
} from "../../../../../../redux/saga/partner-program/engagement-configuration/saga-actions";
import { PartnerEngagementCategoryListType } from "../../../../../../redux/slices/partner-program/engagement-configuration/Types";
import { partnerEngagementConfigurationSliceAction } from "../../../../../../redux/slices/partner-program/engagement-configuration";
import DeleteModal from "../../../../../../common/components/DeleteModal";
import AddEditEngagementCategoryModal from "./addCategoryModal";

type Props = {
  activeTab: number;
  readOnly?: boolean;
};

const EngagementCategory: React.FC<Props> = ({
  activeTab,
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const {
    partnerEngagementCategoryList,
    status: {
      partnerEngagementCategoryListLoading,
      deleteEngagementCategoryLoading,
      deleteEngagementCategoryStatus,
      deleteEngagementCategoryMessage,
    },
  } = useAppSelector((state) => state.partner_engagementConfiguration);

  const [showAdd, setShowAdd] = React.useState(false);
  const [showEdit, setShowEdit] = React.useState(false);
  const [editCategory, setEditCategory] = React.useState<
    PartnerEngagementCategoryListType[number] | null
  >(null);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [deleteCategoryId, setDeleteCategoryId] = React.useState<number | null>(
    null
  );

  useEffect(() => {
    if (activeTab === 4) {
      dispatch(getPartnerEngagementCategoryListSagaAction());
    }
  }, [activeTab, dispatch]);

  const onAddClick = () => setShowAdd(true);

  const onEditClick = (category: PartnerEngagementCategoryListType[number]) => {
    setEditCategory(category);
    setShowEdit(true);
  };

  const onDeleteClick = (id: number) => {
    dispatch(
      partnerEngagementConfigurationSliceAction.resetDeleteEngagementCategoryStatus()
    );
    setDeleteCategoryId(id);
    setDeleteModalOpen(true);
  };

  const onConfirmDelete = (id: number) => {
    dispatch(deleteEngagementCategorySagaAction(id));
  };

  const handleDeleteModalClose = () => {
    dispatch(
      partnerEngagementConfigurationSliceAction.resetDeleteEngagementCategoryStatus()
    );
    setDeleteModalOpen(false);
    setDeleteCategoryId(null);
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          marginTop: "12px",
          marginBottom: "12px",
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Roboto",
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: "16px",
              lineHeight: "20px",
              letterSpacing: "0.2px",
              color: "#0A0A0A",
              marginBottom: "5px",
            }}
          >
            Engagement Category
          </h1>
          <Typography
            variant="h4"
            style={{
              fontFamily: "Roboto",
              fontStyle: "normal",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "18px",
              letterSpacing: "0.2px",
              color: "#4A5565",
            }}
          >
            Manage engagement categories
          </Typography>
        </div>
        <Button
          variant="primary"
          label="Add Category"
          icon={<Iconography icon="plus" />}
          onClick={onAddClick}
          disabled={readOnly}
        />
      </div>

      <div className="pp-table" role="table" aria-label="Engagement Categories">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 120px",
            gap: "8px",
            padding: "8px 16px",
            background: "#F9FAFB",
            border: "1px solid #E5E7EB",
            borderBottom: "1px solid #E5E7EB",
            borderRadius: "6px 6px 0 0",
            fontFamily: "'Roboto'",
            fontWeight: 500,
            color: "#374151",
          }}
          role="rowgroup"
        >
          <div className="pp-th" role="columnheader">
            Category Name
          </div>
          <div className="pp-th col-actions" role="columnheader">
            Actions
          </div>
        </div>

        <div className="pp-table-body" role="rowgroup">
          {partnerEngagementCategoryListLoading ? (
            <div
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Loader variant={LoaderSizes.medium} />
            </div>
          ) : (partnerEngagementCategoryList ?? []).length === 0 ? (
            <div
              style={{
                padding: "16px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              No Engagement Category found
            </div>
          ) : (
            (partnerEngagementCategoryList ?? []).map((category) => (
              <div
                key={category.ID}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 120px",
                  gap: "8px",
                  alignItems: "center",
                  padding: "16px",
                  minHeight: "48px",
                  borderTop: "2px solid #F3F4F6",
                }}
                role="row"
              >
                <div className="pp-td" role="cell" style={{ paddingLeft: "1px" }}>
                  {category.category_name}
                </div>
                <div className="pp-td col-actions" role="cell">
                  <IconButton
                    aria-label={`Edit ${category.category_name}`}
                    onClick={() => onEditClick(category)}
                    disabled={readOnly}
                  >
                    <Iconography icon="pen" size={IconographySizes.Small} />
                  </IconButton>
                  <IconButton
                    aria-label={`Delete ${category.category_name}`}
                    onClick={() => onDeleteClick(category.ID)}
                    disabled={readOnly}
                  >
                    <Iconography
                      icon="trashCan"
                      size={IconographySizes.Small}
                    />
                  </IconButton>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showAdd && (
        <AddEditEngagementCategoryModal
          mode="add"
          open
          onClose={() => setShowAdd(false)}
          onSubmit={(payload: EngagementCategoryPayloadType) => {
            dispatch(
              addEngagementCategorySagaAction({
                category_name: payload.category_name,
              })
            );
          }}
        />
      )}

      {showEdit && editCategory && (
        <AddEditEngagementCategoryModal
          mode="edit"
          open
          onClose={() => {
            setShowEdit(false);
            setEditCategory(null);
          }}
          initialValue={editCategory}
          onSubmit={(payload: EngagementCategoryPayloadType) => {
            dispatch(
              updateEngagementCategorySagaAction({
                ID: editCategory.ID,
                category_name: payload.category_name,
              })
            );
          }}
        />
      )}

      {deleteModalOpen && deleteCategoryId && (
        <DeleteModal
          setDeleteOpen={handleDeleteModalClose as React.Dispatch<React.SetStateAction<boolean>>}
          onDeleteButtonClick={() => onConfirmDelete(deleteCategoryId)}
          deleteButtonLoading={deleteEngagementCategoryLoading}
          deleteResultStatus={deleteEngagementCategoryStatus}
          deleteResultMessage={deleteEngagementCategoryMessage}
          successMessage={
            deleteEngagementCategoryMessage ||
            "Engagement category deleted successfully"
          }
          optionalHeader="Delete Engagement Category"
          message="Are you sure you want to delete this engagement category?"
        />
      )}
    </div>
  );
};

export default EngagementCategory;
